import { useState } from "react";
import { DOCUMENTS, MENU, PROGRAM, EVENT, PROGRAM_PDF, MENU_PDF } from "@/lib/eventData";
import SectionHeader from "@/components/gala/SectionHeader";
import { FileText, Download, Printer, X, Utensils, Clock, MapPin } from "lucide-react";

const ICONS = {
  clock: Clock,
  utensils: Utensils,
  "map-pin": MapPin,
};

export default function DocumentsHub() {
  const [openDoc, setOpenDoc] = useState(null);

  return (
    <section
      id="documents"
      data-testid="documents-section"
      className="section relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="The Resource Vault"
          title="All the Documents You'll Need"
          description="Program details, the full buffet menu and venue directions — view any document instantly, or send it to your printer."
        />

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {DOCUMENTS.map((doc, idx) => {
            const Icon = ICONS[doc.icon] || FileText;
            const pdfFor = { program: PROGRAM_PDF, menu: MENU_PDF };
            const pdfHref = pdfFor[doc.id];
            const isPdf = Boolean(pdfHref);
            const commonContent = (
              <>
                <div className="w-12 h-12 grid place-items-center border border-[#D4AF37]/50 text-[#F3E08A] rounded-sm group-hover:border-[#D4AF37] transition-colors">
                  <Icon size={20} />
                </div>
                <div className="mt-8">
                  <div className="font-display text-[9px] tracking-[0.3em] text-[#9F7E30] mb-2">
                    {doc.type}
                  </div>
                  <div className="font-serif-luxe text-lg text-[#F8F1DC] leading-snug mb-3">
                    {doc.name}
                  </div>
                  <div className="flex items-center justify-between text-xs text-[#A5BCB1]">
                    <span>{doc.size}</span>
                    <span className="inline-flex items-center gap-1.5 text-[#D4AF37] group-hover:text-[#F3E08A]">
                      {isPdf ? "Download" : "View"} <Download size={12} />
                    </span>
                  </div>
                </div>
              </>
            );

            if (isPdf) {
              return (
                <a
                  key={doc.id}
                  data-testid={`doc-card-${doc.id}`}
                  href={pdfHref}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="card-luxe rounded-sm text-left p-6 flex flex-col justify-between min-h-[220px] group rise"
                  style={{ animationDelay: `${idx * 70}ms` }}
                >
                  {commonContent}
                </a>
              );
            }

            return (
              <button
                key={doc.id}
                data-testid={`doc-card-${doc.id}`}
                onClick={() => setOpenDoc(doc.id)}
                className="card-luxe rounded-sm text-left p-6 flex flex-col justify-between min-h-[220px] group rise"
                style={{ animationDelay: `${idx * 70}ms` }}
              >
                {commonContent}
              </button>
            );
          })}
        </div>
      </div>

      {openDoc && (
        <DocumentModal id={openDoc} onClose={() => setOpenDoc(null)} />
      )}
    </section>
  );
}

function DocumentModal({ id, onClose }) {
  const doc = DOCUMENTS.find((d) => d.id === id);
  const print = () => window.print();

  return (
    <div
      data-testid="doc-modal"
      className="fixed inset-0 z-[90] bg-[#0B2820]/95 backdrop-blur-md flex items-start justify-center overflow-y-auto p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="max-w-4xl w-full my-8 bg-[#123A2E] border border-[#D4AF37]/40 rounded-sm gold-frame relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-[#D4AF37]/25 bg-[#123A2E]/95 backdrop-blur">
          <div className="font-display text-[10px] sm:text-xs tracking-[0.3em] text-[#F3E08A]">
            {doc?.type} · {doc?.name}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="doc-modal-print-btn"
              onClick={print}
              className="btn-ghost-gold px-4 py-2 rounded-sm text-[10px] inline-flex items-center gap-2"
            >
              <Printer size={13} /> Print / Save PDF
            </button>
            <button
              data-testid="doc-modal-close-btn"
              onClick={onClose}
              className="w-10 h-10 grid place-items-center border border-[#D4AF37]/40 text-[#F3E08A] hover:border-[#D4AF37] transition rounded-sm"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="print-area p-8 sm:p-12">
          {id === "program" && <ProgramDoc />}
          {id === "menu" && <MenuDoc />}
          {id === "directions" && <DirectionsDoc />}
        </div>
      </div>
    </div>
  );
}

function DocHeader({ title, subtitle }) {
  return (
    <header className="text-center mb-10">
      <div className="font-display text-[10px] tracking-[0.35em] text-[#D4AF37] mb-3">
        HAVANA NIGHTS · YEAR-END GALA 2026
      </div>
      <h1 className="font-serif-luxe text-3xl sm:text-4xl text-[#F8F1DC]">
        {title}
      </h1>
      <p className="mt-3 text-[#E0D5B8] font-serif-luxe italic">{subtitle}</p>
      <div className="mt-6 mx-auto w-24 hairline-gold" />
    </header>
  );
}

function ProgramDoc() {
  return (
    <div>
      <DocHeader
        title="Run of Show & Program"
        subtitle={`${EVENT.date} · ${EVENT.time} · ${EVENT.venue}`}
      />
      <ol className="space-y-6">
        {PROGRAM.map((p, i) => (
          <li key={i} className="grid grid-cols-[80px_1fr] gap-6 items-start">
            <div className="font-display text-lg tracking-[0.2em] text-[#F3E08A]">
              {p.time}
            </div>
            <div>
              <div className="font-serif-luxe text-xl text-[#F8F1DC]">
                {p.title}
              </div>
              {p.desc && (
                <div className="text-sm text-[#E0D5B8] mt-1">{p.desc}</div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function MenuDoc() {
  const groups = [
    { label: "Starters", data: MENU.starters },
    { label: "Main Entrées", data: MENU.mains },
    { label: "Desserts", data: MENU.desserts },
    { label: "Signature Cocktails", data: MENU.cocktails },
  ];
  return (
    <div>
      <DocHeader
        title="The Banquet Menu"
        subtitle="Cuban-French Fusion · Sommelier & Mixologist Pairings"
      />
      <div className="space-y-10">
        {groups.map((g) => (
          <section key={g.label}>
            <div className="deco-divider mb-6">
              <span className="font-display text-[11px] tracking-[0.35em] text-[#F3E08A]">
                {g.label}
              </span>
            </div>
            <div className="space-y-5">
              {g.data.map((d, idx) => (
                <div key={idx}>
                  <div className="flex items-baseline justify-between gap-4">
                    <div className="font-serif-luxe text-lg text-[#F8F1DC]">
                      {d.name}
                    </div>
                    <div className="text-[10px] text-[#9F7E30] font-display tracking-[0.25em]">
                      {d.tags.join(" · ")}
                    </div>
                  </div>
                  <div className="text-sm text-[#E0D5B8] mt-1">{d.desc}</div>
                  {d.pairing && (
                    <div className="text-xs italic text-[#D4AF37] mt-1">
                      {d.pairing}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function DirectionsDoc() {
  return (
    <div>
      <DocHeader
        title="Venue Directions"
        subtitle={`${EVENT.venue} · ${EVENT.address}`}
      />
      <div className="grid md:grid-cols-2 gap-8 text-sm text-[#E0D5B8] leading-relaxed">
        <div>
          <h3 className="font-serif-luxe text-lg text-[#F3E08A] mb-3">
            Arrival
          </h3>
          <p>
            Doors open from 09:00 for a 09:30 red-carpet arrival. Please have
            your name available for the guest register on the way in.
          </p>
          <p className="mt-3">
            Rideshare drop-off: use the main entrance on Umgazi Road. Nearby
            street and lot parking is available around Menlo Park.
          </p>
        </div>
        <div>
          <h3 className="font-serif-luxe text-lg text-[#F3E08A] mb-3">
            Accessibility
          </h3>
          <p>
            Loft at Nine is fully accessible with step-free entrances,
            reserved companion seating and a quiet lounge on the mezzanine.
            Kindly note any accessibility needs on your RSVP.
          </p>
        </div>
      </div>
    </div>
  );
}
